import type { MiniAppAccountChoiceResponse } from '@repo/api/web/models'

import { resolveMiniAppAuthResponse } from '#Auth/support/resolveMiniAppAuthResponse'

import type { PlatformLaunchPayload } from './ports/types'

export type PlatformAuthAttemptResult
  = | { status: 'ok' }
    | { status: 'fail' }
    | { status: 'accountChoice', choice: MiniAppAccountChoiceResponse }

/**
 * POST auth/mini-app with launch `{ payload }` (no `provider`).
 * Caller must have cleared any previous Bearer so a failed POST cannot reuse it.
 */
export async function attemptPlatformAuth(
  payload: PlatformLaunchPayload,
): Promise<PlatformAuthAttemptResult> {
  if (!payload.raw) {
    return { status: 'fail' }
  }

  const { service } = useAuth()

  try {
    const response = await service.authMiniApp(payload.raw)
    const outcome = resolveMiniAppAuthResponse(response)

    switch (outcome.kind) {
      case 'completed':
        await service.applyCompletedSession(outcome.session)
        return { status: 'ok' }
      case 'accountChoice':
        return { status: 'accountChoice', choice: outcome.choice }
      default: {
        const _exhaustive: never = outcome
        void _exhaustive
        return { status: 'fail' }
      }
    }
  }
  catch (error) {
    console.error('[platform-auth] auth/mini-app failed', error)
    return { status: 'fail' }
  }
}

/**
 * POST auth/mini-app/select after Telegram accountChoice.
 */
export async function completePlatformAccountSelect(
  choiceToken: string,
  userId: number,
): Promise<'ok' | 'fail'> {
  const { service } = useAuth()

  try {
    const session = await service.authMiniAppSelect(choiceToken, userId)
    await service.applyCompletedSession(session)
    return 'ok'
  }
  catch (error) {
    console.error('[platform-auth] auth/mini-app/select failed', error)
    return 'fail'
  }
}
